import Backbone from 'backbone'
import _ from 'lodash'
import template from 'scoreboard/dashboards/edit/chart-or-report-dialog-editor.hbs'
import ChartDesignerEditor from 'scoreboard/charts/edit/chart-designer-editor'
import FormView from 'scoreboard/components/form-view'
import renderChartDesigner from 'scoreboard/charts/render-chart-designer'
import renderChartDataTable from 'scoreboard/charts/render-chart-data-table'
import renderChartColorLegend from 'scoreboard/charts/render-chart-color-legend'
import i18n from 'scoreboard/components/i18n'
import $ from 'jquery'
import WidgetEmptyState from 'scoreboard/dashboards/widgets/widget-empty-state'
import DrilldownManager from '../drilldown-manager'

const ChartDialogEditor = Backbone.View.extend({

	/**
	 * @type ChartDesigner
	 */
	model: null,

	tagName: 'form',

	className: 'chart-dialog-editor',

	/**
	 * @param options
	 *
	 * @param {ChartDesignerEditorOptions} [options.chartDesignerEditorOptions]
	 *
	 * @param {Map<Int, Array[ApiDatasetFilter]>} [options.liveFilters] any active
	 * dataset filters that should be applied to the chart, like filter widgets
	 * on a dashboard
	 *
	 * @param {Function} [options.submitSecondary] the function to invoke when
	 * the secondary button is clicked
	 */
	initialize: function(options = {}) {
		this.chartData = options.chartData || this.model.data
		this.options = options
		this.$el.addClass(options.extraClass)

		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: true,
			submitSecondary: this.options.submitSecondary
		})
		_.bindAll(this, 'renderChart')
	},

	render: function() {
		this.$el.empty().append(template({
			title: i18n.get('charts.chart.edit'),
			submitLabel: i18n.get(this.options.submitLabel || 'common.done')
		}))
		this.maxDimensions = true
		this.chartDesignerEditor = new ChartDesignerEditor(_.extend({
			model: this.model,
			data: this.chartData
		}, this.options.chartDesignerEditorOptions))
		this.chartDesignerEditor.on('emphasize', this.renderChart.bind(this))
		this.$('.editor').append(this.chartDesignerEditor.el)
		this.chartDesignerEditor.render()

		this.listenTo(this.chartDesignerEditor, 'change', (fetchRerender) => {
			this.onChange(fetchRerender)
		})
		// Instead of process.nextTick(), we could use the `opened` event provided
		// by the dialog, but it causes a small delay before the chart shows up.
		process.nextTick(() => this.renderChartAndAccessories())
		$(window).on('resize', this.renderChart)

		this.$('.chart-designer-panel').addClass('dialog-panel')

		this.formView.start()

		return this
	},

	renderChart: function (emphasize = null) {
		if (this.chart) {
			this.chart.destroyTooltip()
		}

		if (this.model.get('error')) {
			this.renderError({
				errorResponse: this.model.get('error')
			})
			return
		}

		const container = this.$('.chart-container')
		if (this.options.width) {
			container.css('min-width', this.options.width)
			container.css('width', this.options.width)
		}
		if (this.options.height) {
			container.css('min-height', this.options.height)
			container.css('height', this.options.height)
		}
		// If container has a fixed size, center it
		if (this.options.widget || this.options.height) {
			this.$('.chart-and-legend').css('margin', 'auto')
		}

		this.chart = renderChartDesigner({
			chartDesigner: this.model,
			chartData: this.model.data,
			container,
			// #45789 This function gets called by event handlers, so ignore event
			// objects when they are passed in
			emphasize: (typeof emphasize === 'object') ? null : emphasize
		})

		if (this.chart) {
			this.drilldownManager = new DrilldownManager({
				chartDesigner: this.model,
				chartViewObject: this.chart
			})
			this.listenTo(this.drilldownManager, 'drilldown back',
				drilldown => this.onDrilldown(drilldown))
			this.listenTo(this.drilldownManager, 'rerender',
				() => this.renderChart())
		}

		this.chartDesignerEditor.trigger('resize')
	},

	onDrilldown: function (drilldown) {
		let dataset = this.model.startingDataset()
		let liveFilters = {}
		if (!!dataset) liveFilters[dataset.id] = drilldown.drilldownFilters

		this.model.data.fetch({
			chart: this.model,
			liveFilters,
			drilldownId: drilldown.drilldownId,
			drilldown
		}).then(() => {
			this.renderChartAndAccessories()
		})
	},

	renderError: function (errorOpts = {}) {
		this.removeEmptyState()

		this.emptyState = new WidgetEmptyState(_.extend({}, errorOpts, {
			container: this.$el,
			widgetType: 'chart'
		}))

		this.$('.chart-container').append(this.emptyState.el)
		this.$('.content').addClass('has-widget-error')
		this.emptyState.render()
	},

	removeEmptyState() {
		this.emptyState && this.emptyState.remove()
	},

	/**
	 * Renders the chart and its accessories (data table and legends)
	 */
	renderChartAndAccessories: function () {
		this.renderDataTable()
		this.renderColorLegend()

		this.renderChart() // Redraw the chart to the correct height
	},

	renderDataTable: function() {
		if (this.dataTable) {
			this.dataTable.remove()
		}
		const container = this.$('.chart-table-container .panel-content')
		this.dataTable = renderChartDataTable(container, this.model)
		this.$el.toggleClass('is-showing-table', !!this.dataTable)
	},

	renderColorLegend: function() {
		if (this.colorLegend) {
			this.colorLegend.remove()
		}
		const container = this.$('.chart-legend-container')
		this.colorLegend = renderChartColorLegend(container, this.model)
		this.colorLegend && this.colorLegend.on('emphasize deemphasize',
			this.emphasize.bind(this))
	},

	emphasize: function(seriesId) {
		this.renderChart(seriesId)
	},

	onChange: function ({ fetch, rerender }) {
		this.formView.setDirty()

		if (fetch) {
			this.chartData.fetch({
				chart: this.model,
				liveFilters: this.options.liveFilters
			}).then(() => {
				this.rerender()
			})
		} else if (rerender) {
			this.rerender()
		}
	},

	rerender: function () {
		if (this.removed) {
			// #45649 don't attempt to render if already removed; can happen if
			// a data fetch didn't complete before dialog was closed
			return
		}

		this.renderChartAndAccessories()
		this.chartDesignerEditor.refreshPanels()
	},

	submit: function() {
		this.trigger('done', {
			chartDesigner: this.model,
			chartData: this.model.data
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invoke(this.chart, 'destroy')
		_.invokeMap([this.formView, this.chartDesignerEditor,
			this.dataTable, this.colorLegend, this.drilldownManager], 'remove')
		$(window).off('resize', this.renderChart)
		this.removeEmptyState()
		this.removed = true
	}
})

export default ChartDialogEditor

ChartDialogEditor.__filename__ = 'charts/edit/chart-dialog-editor.js'
